import { ApiError, messageFor } from "@altek/ui/lib/problem";
import { formatBytes } from "@altek/ui/lib/format/number";

/**
 * The application's half of "error code → Turkish sentence".
 *
 * THE SPLIT. The kit's `problem.ts` maps the codes every product built on it shares
 * (`STALE_VERSION`, `PERMISSION_DENIED`, …) and falls back to the server's own text for a code it
 * does not know. The API's catalog (`altek-erp-backend/src/core/errors/catalog.ts`) names codes of
 * its own on top of that — a customer not yet chosen, a refused file type, no office server,
 * folder rules — and RESERVES the ones quota and malware scanning will throw. Until a code moves
 * into the kit, its sentence lives here, in ONE place, not in the feature that first met it.
 *
 * WHAT A PRODUCTION CLIENT RECEIVES (backend `publicDetails`): the `code`; `details.reason` when
 * it is a machine code; and only the numeric or token details each code lists (`maxBytes`,
 * `maxDepth`, `usedBytes`/`quotaBytes`, `retryAfterSeconds`). Everything written below works with
 * exactly that and nothing more — a sentence that needs a file name would read fine in
 * development and break when deployed.
 *
 * THE ORDER `appErrorMessage` ASKS IN:
 * 1. `code` + `details.reason` — the rules the workspace modules express as a broad code plus a
 *    reason (BACKEND_CONTRACT §10). Feature helpers (`chat-errors.ts`, `converter-errors.ts`,
 *    `documents.model.ts`) may still say something more specific; this is the shared default.
 * 2. A detail-aware phrasing for the code, when the number it needs arrived.
 * 3. The code's own sentence.
 * 4. The reason as a code — an older API build sent `UNPROCESSABLE { reason:
 *    "OFFICE_EDITOR_NOT_CONFIGURED" }` for what is now the code itself.
 *
 * HARD RULE: branch on codes, never on `serverMessage` — the server's prose is not a contract.
 */

/** Codes the API throws (or has reserved) that the kit's map does not phrase. */
export type AppErrorCode =
  | "TENANT_SELECTION_REQUIRED"
  | "FILE_TYPE_NOT_ALLOWED"
  | "FILE_TOO_LARGE"
  | "QUOTA_EXCEEDED"
  | "FILE_INFECTED"
  | "FILE_SCAN_FAILED"
  | "FILE_ACTIVE_CONTENT"
  | "FILE_ENCRYPTED"
  | "OFFICE_EDITOR_NOT_CONFIGURED"
  | "OFFICE_EDITOR_UNAVAILABLE"
  | "UNSUPPORTED_DOCUMENT_TYPE"
  | "CLASSIFICATION_REQUIRES_STREAMING"
  | "LINK_RETIRED"
  | "SESSION_CLOSED"
  | "FOLDER_DEPTH_EXCEEDED"
  | "FOLDER_MOVE_INTO_DESCENDANT"
  | "FOLDER_HAS_CHILDREN"
  | "LEGAL_HOLD"
  | "LEGAL_HOLD_EXPIRED_ROW"
  | "NOT_IN_TRASH"
  | "NOTE_BODY_FOREIGN_FILE"
  | "LIMIT_EXCEEDED"
  | "ATTACHMENT_LIMIT"
  | "WORKSPACE_ADMIN_LOCKED"
  | "TEMPLATE_INVALID"
  // F2a 1 Kasım 2026: onay akışı sürümü motorun çalıştıramayacağı bir şey taşıyor — cümle sunucudan.
  | "WORKFLOW_INVALID"
  | "TEMPLATE_UNAVAILABLE"
  | "PDF_RENDERER_NOT_CONFIGURED"
  | "PDF_RENDERER_UNAVAILABLE"
  // belge-duzeltme 20 Ekim 2026 (H2): PDF motoru isteği reddetti (kimlik/istek) — kurulum ayarı.
  | "PDF_RENDERER_REJECTED"
  | "COMPANY_REQUIRED"
  // belge-duzeltme 20 Ekim 2026 (H1): dönüştürme hizmeti tanımlı ama reddediyor/cevap vermiyor.
  | "CONVERTER_PROVIDER_UNAVAILABLE"
  | "BUDGET_LINE_HAS_PAYMENTS"
  // ik-izin 6 Ekim 2026: an approval flow none of whose steps resolves an approver.
  | "APPROVER_NOT_FOUND"
  // 17 Ekim 2026 (#31): the sign-in's second step.
  | "MFA_CODE_INVALID"
  | "MFA_CHALLENGE_INVALID"
  | "MFA_REQUIRED";

const FOLDER_HAS_CHILDREN =
  "Alt klasörleri olan bir klasör silinemez. Önce alt klasörleri taşıyın ya da silin.";

const APP_MESSAGES: Record<AppErrorCode, string> = {
  TENANT_SELECTION_REQUIRED: "Önce çalışacağınız müşteriyi seçin.",

  // -- belge şablonları (`module.templates`) -----------------------------------------------
  /**
   * Yayın reddi. Cümlelerin KENDİSİ buraya gelmiyor — canlıda yalnız `count` gidiyor ve engelleri
   * ekran şablonun kendi `problems` alanından okuyor. Burada sayıyı yazmak da yanlış olurdu:
   * «3 sorun var» diyen bir bildirim, hangileri olduğunu göstermeyen bir bildirimden daha
   * sinir bozucudur.
   */
  TEMPLATE_INVALID: "Şablon yayımlanamadı: giderilmesi gereken sorunlar var. Düzenleyicideki uyarılara bakın.",
  // Yedek: sunucunun cümlesi adımı ve alanı adlandırır (`SERVER_SENTENCE_CODES`) — bu yalnız onu taşımayan cevap için.
  WORKFLOW_INVALID: "Onay akışı yayımlanamıyor: yayından önce giderilmesi gereken sorunlar var.",
  TEMPLATE_UNAVAILABLE: "Bu şablondan yeni belge oluşturulamıyor: şablon arşivlenmiş ya da henüz yayımlanmamış.",
  /**
   * İKİSİ DE «taslağınız duruyor» diyor, ve bu cümlenin yarısı bilgi değil GÜVENCE: kişi uzun bir
   * teklifi doldurmuşken PDF hatası görüyor ve ilk düşündüğü şey yazdıklarının gitmiş olması.
   */
  PDF_RENDERER_NOT_CONFIGURED: "Bu kurulumda PDF üretimi tanımlı değil. Taslağınız kaydedildi; yöneticinize bildirin.",
  PDF_RENDERER_UNAVAILABLE: "PDF üretim hizmetine şu an ulaşılamıyor. Taslağınız kaydedildi; kısa süre sonra yeniden deneyin.",
  /**
   * YENİDEN DENEYİN DEMİYOR (H2): reddin sebebi kurulumun ayarı (PDF motorunun parolası); kişi
   * tekrar bassa da aynı cevabı alır. Eskiden bu durum «Beklenmeyen bir hata oluştu» diyordu.
   */
  PDF_RENDERER_REJECTED:
    "PDF üretim hizmeti isteği reddetti; bu bir kurulum ayarı. Taslağınız kaydedildi — sistem yöneticinize bildirin.",
  CONVERTER_PROVIDER_UNAVAILABLE:
    "Dönüştürme hizmeti şu an kullanılamıyor. Sistem yöneticinize bildirin; düzelince yeniden deneyebilirsiniz.",
  COMPANY_REQUIRED: "Belgenin şirketini seçin: ünvan, adres ve belge numarası şirketten geliyor.",

  // -- files ------------------------------------------------------------------------------
  FILE_TYPE_NOT_ALLOWED: "Bu dosya türü burada kabul edilmiyor. Dosyayı desteklenen bir biçimde yükleyin.",
  FILE_TOO_LARGE: "Dosya izin verilen boyutun üzerinde.",
  QUOTA_EXCEEDED: "Depolama alanınız doldu. Yer açmak için eski dosyalarınızı silin.",
  FILE_INFECTED: "Dosyada zararlı içerik bulundu; dosya reddedildi.",
  FILE_SCAN_FAILED: "Dosya güvenlik taramasından geçirilemedi. Kısa süre sonra tekrar deneyin.",
  FILE_ACTIVE_CONTENT:
    "Makro ya da etkin içerik taşıyan dosyalar kabul edilmiyor. Dosyayı makrosuz kaydedip tekrar yükleyin.",
  FILE_ENCRYPTED:
    "Parola korumalı ya da şifreli dosyalar kabul edilmiyor. Korumayı kaldırıp tekrar yükleyin.",

  // -- documents --------------------------------------------------------------------------
  OFFICE_EDITOR_NOT_CONFIGURED:
    "Bu kurulumda ofis düzenleyici tanımlı değil. Belgeyi indirip bilgisayarınızda düzenleyebilirsiniz; sunucu tarafı düzenleme için sistem yöneticinize başvurun.",
  OFFICE_EDITOR_UNAVAILABLE: "Ofis düzenleyici sunucusuna şu an ulaşılamıyor. Kısa süre sonra tekrar deneyin.",
  UNSUPPORTED_DOCUMENT_TYPE:
    "Bu belge türü düzenleyicide açılamıyor. Belgeyi indirip bilgisayarınızda açabilirsiniz.",
  /*
   * İKİ YOL bu kodu veriyor ve cümle ikisini de karşılamalı: ofis düzenleyicisi ve PDF önizleme
   * kopyası. İkisinde de belge BAŞKA bir sunucuya gidiyor; uygulama içinde akıtılan önizleme
   * (`/files/:id/content`) bu sınıfı zaten açıyor, o yüzden «uygulamada açılamaz» demiyor.
   */
  CLASSIFICATION_REQUIRES_STREAMING:
    "Bu belgenin gizlilik sınıfı, belgenin düzenleyiciye ya da PDF önizleme kopyasına gönderilmesine izin vermiyor. Belgeyi indirip bilgisayarınızda açabilirsiniz.",
  LINK_RETIRED: "Bu belge bağlantısı geri çekilmiş. Kaydın güncel ekini açın.",
  SESSION_CLOSED: "Bu düzenleme oturumu kapatıldı. Belgeyi yeniden açın.",

  // -- folders ----------------------------------------------------------------------------
  FOLDER_DEPTH_EXCEEDED: "Klasör ağacı izin verilen derinliği aşıyor. Klasörü daha üst bir seviyeye taşıyın.",
  FOLDER_MOVE_INTO_DESCENDANT: "Bir klasör kendi alt klasörüne taşınamaz.",
  FOLDER_HAS_CHILDREN,

  // -- saklama ----------------------------------------------------------------------------
  // Soruşturmadan söz etmiyor: hold'un varlığını kime söyleyeceğine bu ekran karar veremez.
  LEGAL_HOLD: "Bu içerik saklama altında olduğu için silinemiyor.",
  // Süresi dolmuş ama kaldırılmamış bir kayıt yeni hold'u engelliyor. «Zaten saklama altında»
  // DEMİYOR, çünkü o satır artık hiçbir şeyi korumuyor — cümle ne yapılacağını söylüyor.
  LEGAL_HOLD_EXPIRED_ROW:
    "Bu özne için süresi dolmuş bir yasal saklama kaydı var. Yeni kayıt açmadan önce o kaydı kaldırın.",
  NOT_IN_TRASH: "Bu kayıt çöp kutusunda değil. Kalıcı silmek için önce çöp kutusuna taşıyın.",
  /**
   * Not gövdesi başkasının yüklediği bir dosyaya işaret ediyor (notlar-v2, sunucunun sahiplik
   * kapısı). Editör kendi yüklediği dosyayı yazıyor; bu cümleyi görmek, gövdeye elle ya da başka
   * bir hesaptan kopyalanmış bir dosya başvurusu demek — kaldırılması gereken o blok.
   */
  NOTE_BODY_FOREIGN_FILE:
    "Notta size ait olmayan bir dosyaya işaret eden bir görsel ya da dosya bloğu var. O bloğu kaldırıp dosyayı kendiniz yükleyin.",

  /**
   * ÇALIŞMA ALANI SINIRLARI (Notlar 14, Diyagramlar 16, K1).
   *
   * Bu iki genel cümle YALNIZ son çare: her sınırın kendi `reason`'ı ve kendi rakamlı cümlesi
   * var (aşağıda). Genel cümle bile «yeriniz doldu» demiyor da «sınıra ulaştınız» diyor, çünkü
   * bir not sınırı depolama kotası değil ve kullanıcıyı dosya silmeye göndermek yanlış olurdu.
   */
  LIMIT_EXCEEDED: "Bu alandaki sınıra ulaştınız. Yer açmak için eski kayıtları silin ya da sistem yöneticinize başvurun.",
  ATTACHMENT_LIMIT: "Bu kayda daha fazla dosya eklenemiyor.",

  /**
   * K2 KAPISI KAPALI. Ekran bu cümleyi normalde HİÇ göstermiyor: `GET /configuration/workspace`
   * `canManage: false` dönüyor ve alanlar kilitli çiziliyor, yani kimse kapalı bir kapıya
   * basmıyor. Yine de burada, çünkü kapı BAŞKA bir sekmede o an çevrilebilir — ekran açıkken
   * SYSTEM_ADMIN anahtarı kapatırsa, kaydet düğmesi bu reddi alır ve sebebini yazması gerekir.
   */
  WORKSPACE_ADMIN_LOCKED:
    "Çalışma alanı ayarlarını değiştirme yetkisi kapalı. Bu ayarı yalnız sistem yöneticisi değiştirebilir.",

  // -- etkinlikler (pz-kongre 6 Ekim 2026, M2) ------------------------------------------------
  /**
   * Ödemesi olan bütçe kalemi silinmiyor: ödeme gerçekten yapılmış bir para ve kalemle birlikte
   * sessizce kaybolması «Ödenen»i geriye doğru değiştirirdi. Cümle ne yapılacağını söylüyor.
   */
  BUDGET_LINE_HAS_PAYMENTS: "Bu bütçe kaleminin ödemesi var; silinemez. Önce kalemin ödemelerini silin.",
  /**
   * ik-izin 6 Ekim 2026 — onay akışının HİÇBİR adımı bir onaycıya çözülmedi (birim başı, üst
   * birim başı ve bir üstü yok). Talep gönderilmiyor; cümle düzeltmenin YERİNİ söylüyor, çünkü
   * düzeltme çalışanın elinde değil: organizasyon şemasında birime yönetici atanması gerekiyor.
   *
   * onay-zinciri 20 Ekim 2026: BU GENEL CÜMLE YEDEK. Sunucu artık hangi birimin neyinin eksik olduğunu
   * söyleyen kendi cümlesini yolluyor («… Genel Müdürlük biriminin yöneticisinin kullanıcı hesabı yok …»,
   * `workflow.gaps.ts`) ve `apiErrorMessage` onu bu kod için tercih ediyor (`SERVER_SENTENCE_CODES`);
   * birim adları istemcide yok, yani cümleyi burada kurmak mümkün değil. Eski bir API ya da sunucunun
   * makine jetonu yolladığı durumda bu cümle okunur. Sunucudaki katalog satırıyla (`catalog.ts`) aynı metin.
   */
  APPROVER_NOT_FOUND:
    "Bu talebi onaylayacak yönetici bulunamadı. Organizasyon şemasında biriminize yönetici atanmalı.",

  // -- iki adımlı doğrulama (17 Ekim 2026, #31) ---------------------------------------------
  /** Yazılan kod tutmadı. Kaç deneme kaldığı `DETAIL_RENDERERS`'ta; oturumla ilgisi yok. */
  MFA_CODE_INVALID: "Doğrulama kodu hatalı. Uygulamanızdaki güncel kodu yazın.",
  /** Sorgu süresi doldu, harcandı, beş hatayla yandı ya da başka cihazdan geldi: baştan giriş. */
  MFA_CHALLENGE_INVALID: "Doğrulama süresi doldu. Yeniden giriş yapın.",
  MFA_REQUIRED: "Bu hesap için iki adımlı doğrulama zorunlu; kapatılamaz.",
};

/**
 * `code:reason` pairs the existing screens already rely on — the shared default sentence for each.
 * The reason is a machine code (`UPPER_SNAKE`); a prose reason is never looked up.
 */
const REASON_MESSAGES: Readonly<Record<string, string>> = {
  // A session whose person can enter NO customer any more (every membership withdrawn). Same code
  // as "choose a customer", so a client that shows the picker on the code must check this first —
  // `tenantRefusalOf` below does.
  "TENANT_SELECTION_REQUIRED:NO_REACHABLE_TENANT":
    "Hesabınız şu an erişebileceğiniz bir müşteriye bağlı değil. Sistem yöneticinize başvurun.",

  // Support sessions refuse personal content by design. NOT "yetkiniz yok": the administrator
  // holds the key, and saying otherwise sends them to ask for a grant they already have.
  "PERMISSION_DENIED:IMPERSONATION_FORBIDDEN": "Destek oturumunda kişisel içerik açılamaz ya da değiştirilemez.",
  "PERMISSION_DENIED:OWNER_REQUIRED": "Bu işlem için kaydın sahibi olmanız gerekiyor.",

  // 17 Ekim 2026 (#16, #21, #31) — yalnız sistem yöneticisinin ve kişinin kendisinin yapamadığı işler.
  "PERMISSION_DENIED:SYSTEM_ADMIN_ONLY": "Bu işlemi yalnız sistem yöneticisi yapabilir.",
  "PERMISSION_DENIED:OWN_ACCOUNT": "Kendi hesabınızda bu işlemi yapamazsınız.",

  /*
   * 29 EKİM 2026 — ORGANİZASYON ŞEMASINA BAĞLI KARARLAR. Aylık plan ve saha görüşmesinde kararı
   * ROL değil ŞEMA veriyor: anahtarı taşıyan ama o kişinin başı olmayan karar veremiyor.
   *
   * CÜMLESİZ BIRAKILMIŞTI ve tarayıcıda görüldü: «Onayla»ya basan yönetici «Beklenmeyen bir hata
   * oluştu» görüyordu — ne olduğunu da, kimin onaylayabileceğini de söylemeyen bir cümle. Yetki
   * reddi bir çökme değil; ekran bunu bir kural olarak söylemeli.
   */
  "PERMISSION_DENIED:NOT_TEAM_LEAD":
    "Bu kaydın kararını yalnız kişinin organizasyon şemasındaki yöneticisi verebilir.",
  "PERMISSION_DENIED:NOT_VISIBLE": "Bu kişinin kaydı size kapalı.",
  "PERMISSION_DENIED:NOT_IN_REACH": "Bu kişinin takvimi size kapalı.",
  "PERMISSION_DENIED:SELF_APPROVAL": "Kendi kaydınızın kararını kendiniz veremezsiniz.",
  "PERMISSION_DENIED:SELF_FULFILMENT":
    "Kendi talebinizi kendiniz karşılayamazsınız; bir meslektaşınız karşılar.",
  "PERMISSION_DENIED:NOT_REQUESTER": "Bu talebi yalnız açan kişi değiştirebilir ya da geri çekebilir.",
  "PERMISSION_DENIED:NOT_PLAN_OWNER": "Bu planı yalnız sahibi gönderebilir.",
  "PERMISSION_DENIED:NOT_AUTHOR": "Bu kaydı yalnız yazan kişi düzeltebilir.",

  /*
   * ik-belge 20 Ekim 2026 — özlük belgesinin değiştirilmesi. «Kendi oluşturduğunuz kaydı onaylayamazsınız» (kitin
   * genel cümlesi) burada yanlış okunurdu: İK müdürü kendi belgesini onaylamaya değil DEĞİŞTİRMEYE çalışıyor.
   */
  "SEPARATION_OF_DUTIES:OWN_DOCUMENT":
    "Kendi belgenizde karar vermek, değiştirmek ya da kaldırmak size kapalı; bir meslektaşınız ya da onay zincirindeki yönetici yapar.",
  "CONFLICT:DOCUMENT_FILE_ALREADY_USED":
    "Bu dosya belgenin güncel ya da önceki sürümü olarak zaten bağlı. Farklı bir dosya seçin.",
  "CONFLICT:DOCUMENT_NOT_CHANGEABLE":
    "Reddedilmiş ya da değiştirilmiş bir belgenin dosyası ve bilgileri değiştirilemez; yalnız kaldırılabilir. Yeni bir belge ekleyin.",
  "CONFLICT:DOCUMENT_TYPE_ON_FILE":
    "Kişinin dosyasında bu türde başka bir belge var. Önce onu değiştirin ya da kaldırın.",
  "MFA_REQUIRED:REQUIRED_BY_ROLE":
    "Rolünüz gereği iki adımlı doğrulama zorunlu; kapatılamaz. Kodlarınızı yenileyebilir ya da uygulamayı değiştirebilirsiniz.",
  "MFA_REQUIRED:REQUIRED_FOR_ACCOUNT":
    "Bu hesap için iki adımlı doğrulama zorunlu tutuluyor; kapatılamaz.",
  "SESSION_REVOKED:SECOND_FACTOR_REQUIRED":
    "Bu hesap için iki adımlı doğrulama artık gerekli. Yeniden giriş yapın; kurulum adımları gösterilecek.",
  "CONFLICT:CHAIN_INTACT": "Denetim zinciri zaten sağlam; çapalanacak bir kırık yok.",

  // ekip geri bildirimi (rol-yetki, 21 Ekim 2026) — yazan yönetici, kendisi hakkında yazamaz; geri çekme bir kez.
  "PERMISSION_DENIED:SELF_FEEDBACK": "Kendiniz hakkında geri bildirim yazamazsınız.",
  "PERMISSION_DENIED:EMPLOYMENT_REQUIRED":
    "Geri bildirim yazmak için aktif bir istihdam kaydınız olmalı; kaydınız başlamadıysa İK ile görüşün.",
  "CONFLICT:FEEDBACK_ALREADY_RETRACTED": "Bu geri bildirim zaten geri çekilmiş. Listeyi yenileyin.",

  // izin (ik-izin, 6 Ekim 2026)
  /** Kişi kendi onaylı iznini ancak başlamadan iptal edebiliyor; başladıysa karar yöneticinin. */
  "IMMUTABLE_RECORD:LEAVE_ALREADY_STARTED":
    "Başlamış bir izin iptal edilemez. Kullanmadığınız günler için yöneticinizle görüşün.",
  /**
   * Açılış kaydı (geçiş öncesi kullanım) toplam hak edişi aşıyor. Çoğu zaman geçmiş dönemlerin
   * hak edişi henüz yazılmamıştır — cümle önce onu öneriyor.
   */
  "LEAVE_BALANCE_INSUFFICIENT:OPENING_EXCEEDS_ENTITLEMENT":
    "Girilen kullanım, çalışanın toplam hak edişinden fazla. Önce «Hak edişleri güncelle» ile geçmiş yılları işleyin ya da gün sayısını kontrol edin.",

  // istihdam yaşam döngüsü (ik-yasam-dongusu, 19 Ekim 2026) — çıkış planı, işe başlatma, silme.
  "CONFLICT:EXIT_ALREADY_PLANNED":
    "Bu sözleşmenin çıkışı zaten planlandı. Tarihi değiştirmek için önce planlanan çıkışı geri alın.",
  "CONFLICT:EXIT_IN_EFFECT":
    "Bu çıkış yürürlüğe girdi; geri alınamaz. Kişi dönüyorsa yeni bir sözleşme açın.",
  "CONFLICT:NO_PLANNED_EXIT": "Bu sözleşmenin planlanmış bir çıkışı yok.",
  "INVALID_STATE_TRANSITION:START_DATE_AHEAD":
    "İşe giriş tarihi henüz gelmedi; sözleşme o gün başlatılabilir. Tarih yanlışsa önce düzeltin.",
  "IN_USE:EMPLOYMENT_HAS_OPEN_ASSETS":
    "Bu sözleşmede iade edilmemiş zimmet var. Önce zimmeti iade alın; geçmişi olan sözleşme silinmez, çıkışı kaydedilir.",
  "IN_USE:EMPLOYMENT_HAS_OPEN_VEHICLES":
    "Bu sözleşmede teslim edilmemiş araç var. Önce aracı teslim alın; geçmişi olan sözleşme silinmez, çıkışı kaydedilir.",
  // 31 Ekim 2026: atama satırları iz — bir kez atanmış rol silinmiyor (`AuthorizationAdminService.deleteRole`).
  "IN_USE:ROLE_HAS_ASSIGNMENT_HISTORY":
    "Bu rol geçmişte atanmış; atama geçmişi korunduğu için silinemez. Kullanılmasın istiyorsanız pasife alın.",
  "IN_USE:EMPLOYMENT_HAS_HISTORY":
    "Bu sözleşmenin geçmiş kaydı var (izin, zimmet, eğitim ya da belge); silinemez. Kişi ayrıldıysa çıkışını kaydedin.",
  // «Yeniden yönlendir» (onay-zinciri, 20 Ekim 2026) — takılmış onayı bugünkü şemaya göre yeniden çözmek.
  "CONFLICT:APPROVAL_NOT_STUCK":
    "Bu talebin onaycısı görevi açabiliyor; yeniden yönlendirmeye gerek yok. Listeyi yenileyin.",
  "CONFLICT:APPROVAL_NOT_PENDING": "Bu talebin onayı artık sürmüyor. Listeyi yenileyin.",
  "CONFLICT:APPROVAL_PARTLY_ACTIVE":
    "Adımın onaycılarından bazıları hâlâ karar verebiliyor; talep yönlendirilmedi.",
  // resmî tatiller (ik-yasam-dongusu, 19 Ekim 2026, İK-16)
  "UNPROCESSABLE:YEAR_NOT_LISTED":
    "Bu yılın resmî tatil listesi tanımlı değil; günleri «Tatil ekle» ile tek tek girin.",

  // kullanıcılar (7 Ekim 2026) — «Giriş kilidini kaldır» yalnız etkin ve kilitli hesapta işler;
  // kapatılmış, askıya alınmış ya da davet bekleyen hesap `unlockUser`'da 409 alıyor.
  "CONFLICT:USER_NOT_UNLOCKABLE":
    "Bu hesap kilitli değil; kapatılmış ya da askıya alınmış bir hesap kilit açmayla etkinleşmez.",

  // chat
  "CONFLICT:CONVERSATION_ARCHIVED": "Bu sohbet arşivde. Önce arşivden çıkarın.",
  /*
   * `UNPROCESSABLE:SELF_CONVERSATION` BURADAN KALDIRILDI (18 Eylül 2026).
   *
   * Sunucu kendiyle sohbeti yasaklamayı bıraktı — not defteri olarak açılıyor (`chat.rules.ts`,
   * BACKEND_CONTRACT §10) — ve sözleşme «istemcide eşlemesi yok» diyordu, ama eşleme buradaydı.
   * Sohbetin kendi yardımcısı `messageFor`'da bittiği için hiç okunmuyordu; sohbet
   * `apiErrorMessage`'a bağlanınca ortaya çıktı. İzin verilen bir şeyi yasak diye anlatan bir
   * cümle, hiç cümle olmamasından kötüdür.
   */
  "UNPROCESSABLE:NO_MEMBERS": "Gruba en az bir kişi eklemelisiniz.",

  // converter
  "UNPROCESSABLE:PROVIDER_NOT_CONFIGURED":
    "Bu kurulumda belge dönüştürme servisi tanımlı değil. Sistem yöneticinize başvurun.",
  "UNPROCESSABLE:OPERATION_NOT_SUPPORTED":
    "Bu dosya türü seçilen işlemle dönüştürülemiyor. Başka bir işlem veya hedef biçim seçin.",
  "UNPROCESSABLE:UNSUPPORTED_SOURCE_TYPE":
    "Bu dosya türü dönüştürülemiyor. Dosyayı desteklenen bir biçimde yükleyin.",
  "UNPROCESSABLE:CLASSIFICATION_NOT_ALLOWED": "Bu dosyanın gizlilik sınıfı dönüştürmeye kapalı.",

  // folders — notes still says this as IN_USE + reason; documents moved to FOLDER_HAS_CHILDREN
  "IN_USE:HAS_CHILD_FOLDERS": FOLDER_HAS_CHILDREN,

  // files
  "FILE_TYPE_NOT_ALLOWED:EXTENSION_MISMATCH":
    "Dosya uzantısı dosyanın türüyle uyuşmuyor. Dosyayı doğru uzantıyla kaydedip tekrar yükleyin.",

  /**
   * Tarama hattının gerekçeleri (K3). Kod zaten bir cümle söylüyor; bunlar KİŞİNİN YAPABİLECEĞİ
   * ŞEYİ ekliyor — «makro» ile «şifreli» arasındaki fark, kullanıcı için «neyi düzeltmeliyim»
   * farkıdır. Gerekçesi eşleşmeyen bir ret kodun kendi cümlesine düşüyor.
   */
  "FILE_TYPE_NOT_ALLOWED:TYPE_MISMATCH":
    "Dosyanın içeriği adının söylediği türle uyuşmuyor. Dosyayı doğru biçimde kaydedip tekrar yükleyin.",
  "FILE_TYPE_NOT_ALLOWED:DOCUMENT_TYPE_CHANGED": "Yeni sürüm belgenin özgün türüyle aynı olmalı.",
  /** dosyalar-v2: «Teklif.docx» → «Teklif.pdf» yeniden adlandırması (Dosyalarım 1). */
  "FILE_TYPE_NOT_ALLOWED:RENAME_CHANGES_TYPE":
    "Belgenin adı türünü değiştiremez: uzantı aynı kalmalı. Başka bir türe çevirmek için Dönüştürücü'yü kullanın.",
  "FILE_ACTIVE_CONTENT:MACRO":
    "Dosya makro içeriyor. Makrosuz bir kopyasını (ör. .docx / .xlsx) kaydedip tekrar yükleyin.",
  "FILE_ACTIVE_CONTENT:MACRO_SHEET":
    "Dosya makro sayfası içeriyor. Makrosuz bir kopyasını kaydedip tekrar yükleyin.",
  "FILE_ACTIVE_CONTENT:OLE_OBJECT": "Dosya gömülü bir nesne içeriyor; bu tür ekler kabul edilmiyor.",
  "FILE_ACTIVE_CONTENT:EXTERNAL_LINK":
    "Dosya dışarıdan içerik çağıran bir bağlantı içeriyor. Bağlantıyı kaldırıp tekrar yükleyin.",
  "FILE_ACTIVE_CONTENT:PDF_JAVASCRIPT": "PDF betik içeriyor; betikli PDF'ler kabul edilmiyor.",
  "FILE_ACTIVE_CONTENT:PDF_OPEN_ACTION": "PDF açılışta bir eylem çalıştırıyor; bu tür PDF'ler kabul edilmiyor.",
  "FILE_ACTIVE_CONTENT:PDF_EMBEDDED_FILE": "PDF gömülü bir dosya taşıyor; bu tür PDF'ler kabul edilmiyor.",
  "FILE_ENCRYPTED:ENCRYPTED_PDF": "PDF parola korumalı. Korumayı kaldırıp tekrar yükleyin.",
  "FILE_ENCRYPTED:ENCRYPTED_OFFICE": "Belge parola korumalı. Korumayı kaldırıp tekrar yükleyin.",
  "FILE_ENCRYPTED:ENCRYPTED_ARCHIVE": "Arşiv parola korumalı. Korumasız bir kopyasını yükleyin.",
  /**
   * belge-duzeltme 20 Ekim 2026 (H17): şifreli PDF'i çoğu zaman bizim denetimimizden ÖNCE ClamAV'ın
   * sezgisel imzası yakalıyor (`Heuristics.Encrypted.*` → `SCANNER_ENCRYPTED`). Kişiye «zararlı»
   * değil, ne yapacağı söyleniyor.
   */
  "FILE_ENCRYPTED:SCANNER_ENCRYPTED":
    "Dosya parola korumalı (şifreli); parola korumalı dosyalar kabul edilmiyor. Parolayı kaldırıp tekrar yükleyin.",
  "FILE_TOO_LARGE:SCANNER_LIMIT":
    "Dosya, güvenlik taramasının açabileceği sınırı aşıyor (çok büyük ya da iç içe arşiv). Daha küçük bir kopyasını yükleyin.",
  "FILE_TOO_LARGE:ARCHIVE_BOMB": "Dosyanın açılmış içeriği izin verilen sınırı aşıyor.",
  "FILE_TOO_LARGE:PIXEL_BOMB": "Görselin boyutu işlenebilecek sınırın üzerinde.",
  "FILE_SCAN_FAILED:UNREACHABLE":
    "Güvenlik taraması şu an yapılamıyor; dosya kullanıma açılmadı. Kısa süre sonra tekrar deneyin.",
  /**
   * «Tekrar deneyin» DEMEYEN iki gerekçe: ikisinde de aynı dosya hiçbir zaman taranamayacak, yani
   * kullanıcıyı beklemeye çağırmak yanlış yönlendirmek olurdu.
   */
  "FILE_SCAN_FAILED:STREAM_TOO_LARGE":
    "Dosya, güvenlik taramasının işleyebileceği boyutun üzerinde; kullanıma açılamıyor. Daha küçük bir kopyasını yükleyin.",
  "FILE_SCAN_FAILED:MAX_ATTEMPTS":
    "Dosya birkaç denemede taranamadı ve kullanıma açılmadı. Dosyayı yeniden yükleyin; sorun sürerse sistem yöneticinize bildirin.",
  "FILE_ACTIVE_CONTENT:PDF_UNINSPECTABLE":
    "PDF'in bir bölümü denetlenemedi; güvenli olduğu doğrulanamayan PDF'ler kabul edilmiyor. Dosyayı yeniden kaydedip (ör. «PDF olarak yazdır») tekrar yükleyin.",
  "FILE_ACTIVE_CONTENT:SCANNER_MACRO":
    "Güvenlik taraması dosyada makro buldu. Makrosuz bir kopyasını kaydedip tekrar yükleyin.",
  "FILE_ACTIVE_CONTENT:SCANNER_HEURISTIC":
    "Güvenlik taraması dosyayı kurallara aykırı buldu; dosya reddedildi.",
};

type Details = Readonly<Record<string, unknown>> | null | undefined;

const MACHINE_REASON = /^[A-Z][A-Z0-9_]{1,63}$/;

/**
 * Bir sayı alanı — SAYI YA DA DİZE olarak.
 *
 * Bayt alanları sözleşmede DİZE (`files.quota.ts`: bir kota terabayta çıkabiliyor ve JSON sayısı
 * o noktada güvenilir değil). Yalnız `typeof value === "number"` arayan bir okuyucu onları
 * görmezden gelir ve kullanıcı, rakamları olan cümle yerine genel cümleyi görür — yani ekran
 * «ne kadarını kullandım» sorusunu tam da sorulduğu anda cevaplayamaz. Sayı sınırları
 * (`limit`, `used`) JSON sayısı olarak geliyor; ikisini de aynı okuyucu karşılıyor.
 */
function numberOf(details: Details, key: string): number | null {
  const value = details?.[key];
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Sayıyı Türkçe biçimde: «2.000». Binlik ayracı olmadan 2000 ile 20000 ayırt edilmiyor. */
function count(value: number): string {
  return value.toLocaleString("tr-TR");
}

/** Detail-aware phrasings — used only when the number they need actually arrived. */
const DETAIL_RENDERERS: Partial<Record<AppErrorCode, (details: Details) => string | null>> = {
  BUDGET_LINE_HAS_PAYMENTS: (details) => {
    const payments = numberOf(details, "count");
    return payments === null || payments < 1
      ? null
      : `Bu bütçe kaleminin ${count(payments)} ödemesi var; silinemez. Önce kalemin ödemelerini silin.`;
  },
  FILE_TOO_LARGE: (details) => {
    const maxBytes = numberOf(details, "maxBytes");
    return maxBytes === null ? null : `Dosya çok büyük. En fazla ${formatBytes(maxBytes)} yükleyebilirsiniz.`;
  },
  FOLDER_DEPTH_EXCEEDED: (details) => {
    const maxDepth = numberOf(details, "maxDepth");
    return maxDepth === null
      ? null
      : `Klasör ağacı en fazla ${maxDepth} seviye olabilir. Klasörü daha üst bir seviyeye taşıyın.`;
  },
  QUOTA_EXCEEDED: (details) => {
    const used = numberOf(details, "usedBytes");
    const quota = numberOf(details, "quotaBytes");
    return used === null || quota === null
      ? null
      : `Depolama alanınız doldu (${formatBytes(used)} / ${formatBytes(quota)}). Yer açmak için eski dosyalarınızı silin.`;
  },
  ATTACHMENT_LIMIT: (details) => {
    const limit = numberOf(details, "limit");
    return limit === null ? null : `Bu kayda en fazla ${count(limit)} dosya eklenebilir.`;
  },
  /**
   * SINIRIN HANGİSİ OLDUĞUNU `reason` SÖYLÜYOR, ve her birinin cümlesi kullanıcının o an
   * yapabileceği şeyi anlatıyor: not sayısı doluysa çöpü boşaltmak, gövde büyükse kısaltmak.
   * Tek bir «sınıra ulaştınız» cümlesi, kullanıcıyı hangi sınıra çarptığını tahmin etmeye
   * bırakırdı — ve iki sınırın çözümü aynı değil.
   */
  /** Kaç deneme kaldığını söyler; sıfırsa sorgu yandı, baştan giriş gerekir. */
  MFA_CODE_INVALID: (details) => {
    const left = numberOf(details, "attemptsRemaining");
    if (left === null) return null;
    if (left <= 0) return "Doğrulama kodu hatalı ve deneme hakkı bitti. Yeniden giriş yapın.";
    return `Doğrulama kodu hatalı. ${count(left)} deneme hakkınız kaldı.`;
  },
  LIMIT_EXCEEDED: (details) => {
    const reason = typeof details?.reason === "string" ? details.reason : "";
    const limit = numberOf(details, "limit");
    const maxBytes = numberOf(details, "maxBytes");

    if (reason === "NOTE_COUNT_LIMIT" && limit !== null) {
      return `Not sayısı sınırına ulaştınız (${count(limit)} not). Yeni not açmak için çöp kutusundaki notları kalıcı olarak silin.`;
    }
    if (reason === "NOTE_BODY_LIMIT" && limit !== null) {
      return `Not gövdesi çok uzun. Bir not en fazla ${formatBytes(limit)} olabilir; notu kısaltın ya da ikiye bölün.`;
    }
    if (reason === "DIAGRAM_COUNT_LIMIT" && limit !== null) {
      return `Diyagram sayısı sınırına ulaştınız (${count(limit)} diyagram). Yeni diyagram için çöp kutusunu boşaltın.`;
    }
    if (reason === "MODEL_TOO_LARGE" && maxBytes !== null) {
      return `Çizim çok büyük. Bu diyagram türü en fazla ${formatBytes(maxBytes)} olabilir; bazı öğeleri silip tekrar deneyin.`;
    }
    // belge-duzeltme 20 Ekim 2026 (H15): metin ve CSV → PDF'in kendi tavanı.
    const maxMb = numberOf(details, "maxMb");
    if (reason === "TEXT_PDF_LIMIT") {
      return maxMb === null
        ? "Metin ve CSV dosyaları bu boyutta PDF'e dönüştürülemiyor. Dosyayı bölüp parça parça dönüştürün."
        : `Metin ve CSV dosyaları PDF'e en fazla ${count(maxMb)} MB olarak dönüştürülebiliyor. Dosyayı bölüp parça parça dönüştürün.`;
    }
    if (reason === "NAMED_REVISION_LIMIT" && limit !== null) {
      return `En fazla ${count(limit)} adlandırılmış sürüm tutulabilir. Yer açmak için birinin adını kaldırın.`;
    }
    return null;
  },
};

function isAppCode(code: string): code is AppErrorCode {
  return Object.prototype.hasOwnProperty.call(APP_MESSAGES, code);
}

/** True when this application, not the kit, owns the sentence for `code`. */
export function isAppErrorCode(code: string): boolean {
  return isAppCode(code);
}

/**
 * The application's sentence for a code (and its details), or `null` when it has nothing more
 * specific than the kit's map — the caller then asks `messageFor`.
 */
export function appErrorMessage(code: string, details: Details): string | null {
  const reason = details?.reason;
  const machineReason = typeof reason === "string" && MACHINE_REASON.test(reason) ? reason : null;

  if (machineReason) {
    const paired = REASON_MESSAGES[`${code}:${machineReason}`];
    if (paired) return paired;
  }
  if (isAppCode(code)) {
    return DETAIL_RENDERERS[code]?.(details) ?? APP_MESSAGES[code];
  }
  if (machineReason && isAppCode(machineReason)) return APP_MESSAGES[machineReason];
  return null;
}

/**
 * What a `409 TENANT_SELECTION_REQUIRED` asks of the shell, or `null` for any other failure.
 *
 * - `"choose"` — the person may enter several customers and has not chosen: open the picker.
 * - `"none"` — there is no customer left to choose (`details.reason: NO_REACHABLE_TENANT`): a
 *   picker would have no rows; show the sentence and offer signing out.
 *
 * A stale remembered choice never arrives here: the API treats it as no choice, resolves a single
 * remaining customer silently and forgets the pointer.
 */
export function tenantRefusalOf(error: unknown): "choose" | "none" | null {
  if (!(error instanceof ApiError) || error.code !== "TENANT_SELECTION_REQUIRED") return null;
  return error.details?.reason === "NO_REACHABLE_TENANT" ? "none" : "choose";
}

/**
 * Codes whose SENTENCE comes from the server, built from data the client does not have.
 *
 * `APPROVER_NOT_FOUND` names the unit and what is wrong with its head («İnsan Kaynakları biriminin
 * yöneticisi talebi açan kişinin kendisi, üstündeki Genel Müdürlük biriminin yöneticisinin kullanıcı hesabı
 * yok»). Unit names live in the organisation chart, not here, so for these codes the server's own text is
 * shown and the application's line (`APP_MESSAGES`) is only the fallback. Anything else keeps the order:
 * the application's sentence first (it reads numbers, knows what to do next), then the kit's.
 *
 * `WORKFLOW_INVALID` (F2a, 1 Kasım 2026) names the step and the field a flow cannot be published with
 * (««PATRONA_BILDIR» adımının koşulu «amountTRY» alanını okuyor; … bu alanı ilan etmemiş») — the chain
 * editor's own words, which only the server's check (`workflow.flow-check.ts`) has.
 */
const SERVER_SENTENCE_CODES: ReadonlySet<string> = new Set(["APPROVER_NOT_FOUND", "WORKFLOW_INVALID"]);

/**
 * True for a code whose server sentence the client must NOT replace with its own. `parseError`
 * (`client.ts`) turns the server's text of every code this application phrases into the application's
 * sentence so screens calling `messageFor(error)` read it; for these codes that would throw away exactly the
 * part the server knows — the unit names — so the text stays.
 */
export function keepsServerSentence(code: string): boolean {
  return SERVER_SENTENCE_CODES.has(code);
}

/** The server's sentence when the kit judged it written for a person (`ApiError.message` equals it), else null. */
function serverSentenceOf(error: ApiError): string | null {
  const text = error.serverMessage?.trim();
  return text && error.message === text ? text : null;
}

/** The sentence to show for any failure: the application's first, then the kit's. */
export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const sentence = SERVER_SENTENCE_CODES.has(error.code) ? serverSentenceOf(error) : null;
    return sentence ?? appErrorMessage(error.code, error.details) ?? messageFor(error);
  }
  return messageFor(error);
}
